import { ChangeEvent, useRef, useState } from "react";
import { IAppViewItem } from "shared/components";
import { listViewSections } from "./CustomListAppView.config";
import {
  EnumOwnerType,
  ListManyQuery,
  SortFindManyListInput,
  useListManyQuery,
  useListUpdateByIdMutation,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { useLoggedInAs } from "shared/hooks";
import { getFilteredLists } from "./getFilteredLists";
import { getMultilingualString } from "shared/utils";
import { useToastContext } from "@ttc3k/trekker";

export const useCustomLists = () => {
  const [appViewSections, setAppViewSections] = useState(listViewSections);
  const [selectedAppItem, setSelectedAppItem] = useState<IAppViewItem>(
    appViewSections[0].items[0],
  );

  const { t } = useTranslation();
  const loggedInAs = useLoggedInAs();
  const { toastFactory } = useToastContext();

  /**
   * Track the originally fetched lists (this list is mutated as lists are favorited/unfavorited)
   * AND
   * handle UI updates (such as the counts for favorited, archived, all)
   */
  const allLists = useRef<ListManyQuery["listMany"]>([]);
  const updateFilteredListsAndAppViewSections = () => {
    setFilteredLists([
      ...getFilteredLists(allLists.current, selectedAppItem.key),
    ]);
    setAppViewSections((prev) =>
      prev.map((section) => ({
        items: section.items.map((item) => ({
          ...item,
          text: t(`customLists:HEADER.VIEWS.${item.key}.TITLE`),
          count: getFilteredLists(allLists.current, item.key).length ?? 0,
          isSelected: selectedAppItem?.key === item.key,
        })),
      })),
    );
  };

  // Load the lists, apply view filtering, and update view counts
  const { data, loading, error } = useListManyQuery({
    variables: {
      filter: {
        owner: {
          entityId: loggedInAs?.entityId,
          type: EnumOwnerType.Organization,
        },
      },
      limit: 10000,
      sort: SortFindManyListInput.UpdatedDesc,
    },
    onCompleted: (data) => {
      const lists = data.listMany ?? [];
      allLists.current = lists;
      updateFilteredListsAndAppViewSections();
    },
    // This will ensure that our `onCompleted` re-runs while a refetch is happening
    notifyOnNetworkStatusChange: true,
  });

  const [filteredLists, setFilteredLists] = useState<ListManyQuery["listMany"]>(
    data?.listMany ?? [],
  );
  const [searchValue, setSearchValue] = useState("");

  const handleSearchValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchVal = e.target.value;
    setSearchValue(searchVal);
    const lists = getFilteredLists(
      data?.listMany ?? [],
      selectedAppItem.key,
      e.target.value,
    );
    setFilteredLists([...lists]);
  };

  const handleAppViewChange = (selectedItem: IAppViewItem) => {
    const lists = getFilteredLists(allLists.current ?? [], selectedItem.key);
    setFilteredLists([...lists]);
    setSelectedAppItem(selectedItem);
    setAppViewSections((prev) =>
      prev.map((section) => ({
        items: section.items.map((item) => ({
          ...item,
          isSelected: selectedItem.key === item.key,
        })),
      })),
    );
  };

  /**
   * Handle adding a list to or removing a list from the favorites
   * - While updating, the star icon is disabled in the ListCard
   */
  const [updatingListId, setUpdatingListId] = useState("");
  const [listUpdateByIdMutation] = useListUpdateByIdMutation();

  const handleFavouriteClick = (list: ListManyQuery["listMany"][0]) => {
    const favorited = !list.favorited;
    setUpdatingListId(list._id);
    listUpdateByIdMutation({
      variables: {
        id: list._id,
        record: {
          favorited,
        },
      },
    })
      .then((response) => {
        if (!response.errors) {
          allLists.current = allLists.current.map((item) =>
            item._id !== list._id
              ? item
              : {
                  ...item,
                  favorited,
                },
          );
          updateFilteredListsAndAppViewSections();

          toastFactory.create({
            title: t("core:SUCCESS"),
            description: t("core:FAVORITES.ACTION.SUCCESS", {
              addedOrRemoved: favorited
                ? t("core:FAVORITES.ACTION.ADDED_TO")
                : t("core:FAVORITES.ACTION.REMOVED_FROM"),
              listName: getMultilingualString(list.name),
            }),
          });
        } else {
          toastFactory.create({
            title: t("core:ERROR.TITLE"),
            description: `${t("core:FAVORITES.ACTION.ERROR", { listName: getMultilingualString(list.name) })}. ${t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
          });
        }
      })
      .catch(() => {
        toastFactory.create({
          title: t("core:ERROR.TITLE"),
          description: `${t("core:FAVORITES.ACTION.ERROR", { listName: getMultilingualString(list.name) })}. ${t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
        });
      })
      .finally(() => setUpdatingListId(""));
  };

  return {
    appViewSections,
    selectedAppItem,
    onAppViewChange: handleAppViewChange,
    loading,
    error,
    lists: filteredLists,
    searchValue,
    onSearchValueChange: handleSearchValueChange,
    onFavouriteClick: handleFavouriteClick,
    updatingListId,
  };
};
